import { format, parseISO } from 'date-fns';
import { type FC } from 'react';

import { Esg } from '@/api/esg/models/Esg';
import { WithGap } from '@/components/WithGap';
import { useTexts } from '@/hooks/useTexts';
import { fmsWithTemplate } from '@/i18n/fmsWithTemplate';
import { useUserDateFormat } from '@/i18n/hooks';
import { EsgSolutionsChart } from '@/pages/Esg/widgets/EsgSolutions/EsgSolutionsChart';
import { WithPortfolioGroupEsgData } from '@/pages/Esg/WithPortfolioGroupEsgData';

import { A4WidthForPrint } from '../components/A4WidthForPrint';
import { AvoidPageBreaksInside } from '../components/AvoidPageBreakInside';
import { HeaderContainer } from '../components/HeaderContainer';
import { StyledStorebrandLogo } from '../components/StyledStorebrandLogo';
import { usePresentationContext } from '../PresentationContext';

export const EsgSolutionsScreen: FC = () => {
  const texts = useTexts();
  const dateFormat = useUserDateFormat();

  const { globalFilter } = usePresentationContext();

  return (
    <A4WidthForPrint>
      <WithGap data-test="esg-solutions-screen">
        <HeaderContainer>
          <h1>{texts.pages.presentation.screens.esg.solutions}</h1>
          <StyledStorebrandLogo />
        </HeaderContainer>

        <div className="font-body">{texts.pages.esg.solutions.description}</div>

        <WithGap gap="48">
          {globalFilter.portfolioGroups.map((portfolioGroup) => (
            <AvoidPageBreaksInside key={portfolioGroup.id}>
              <WithGap gap="16">
                <h2>{`${portfolioGroup.id} - ${portfolioGroup.name}`}</h2>

                <WithPortfolioGroupEsgData
                  key={portfolioGroup.id}
                  portfolioGroupId={portfolioGroup.id}
                  render={(esgData: Esg) => (
                    <>
                      <div className="font-details">
                        {fmsWithTemplate(texts.common.lastUpdated, {
                          date: format(
                            parseISO(esgData.updatedDate),
                            dateFormat,
                          ),
                        })}
                      </div>

                      <EsgSolutionsChart
                        greenBond={esgData.greenBond}
                        solutionCompanies={esgData.solutionCompanies}
                        solutionCompaniesEqualOpportunities={
                          esgData.solutionCompaniesEqualOpportunities
                        }
                        solutionCompaniesRenewableEnergy={
                          esgData.solutionCompaniesRenewableEnergy
                        }
                        solutionCompaniesCircularEconomy={
                          esgData.solutionCompaniesCircularEconomy
                        }
                        solutionCompaniesSmartCities={
                          esgData.solutionCompaniesSmartCities
                        }
                        positionLegendBottom
                      />
                    </>
                  )}
                />
              </WithGap>
            </AvoidPageBreaksInside>
          ))}
        </WithGap>
      </WithGap>
    </A4WidthForPrint>
  );
};
